import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { ActivityIndicator, Platform, Pressable, View } from 'react-native';

import { TeamPlanCalculator } from '@/components/subscription/team-plan-calculator';
import { ThemedText } from '@/components/themed-text';
import { useMembership } from '@/hooks/use-membership';
import { usePlanUsage } from '@/hooks/use-provider';
import { useBackToSolo, useOrganization, useTeamMembers } from '@/hooks/use-team';
import {
  useRedistributeAppleTeamPlan,
  useTeamPricing,
  useUpdateTeamSubscription,
} from '@/hooks/use-team-pricing';
import type { UseAppleIAPReturn } from '@/hooks/useAppleIAP';
import { AppleLadderStep, TeamSelection } from '@/lib/team-pricing';
import { useAuthStore } from '@/store/authStore';
import { showToast } from '@/store/toastStore';
import { getApiErrorMessage } from '@/utils/api-error';

function toCount(value: string | undefined): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

/**
 * Planos da organização: a calculadora no lugar dos cartões solo. Contratar a
 * primeira vez vai para o checkout (Stripe) ou compra o degrau (iPhone);
 * quem já tem plano de equipe só troca as quantidades.
 *
 * `?professionals=&clients=` abre já com o número pedido (aviso de assento).
 */
export function TeamPlansPanel({ appleIAP }: { appleIAP: UseAppleIAPReturn }) {
  const params = useLocalSearchParams<{ professionals?: string; clients?: string }>();
  const { data: pricing, isLoading, error } = useTeamPricing();
  const { data: organization } = useOrganization();
  const { data: members } = useTeamMembers();
  const { data: usage } = usePlanUsage();
  const updateSubscription = useUpdateTeamSubscription();
  const redistribute = useRedistributeAppleTeamPlan();
  const { teamPlanPending } = useMembership();
  const backToSolo = useBackToSolo();

  // Sem contrato de equipe, o uso ainda responde pelo plano solo de antes.
  const soloPlanName =
    usage?.planName && !['Sem plano', 'Sem limite'].includes(usage.planName)
      ? usage.planName
      : null;

  // Virou organização e desistiu antes de pagar: volta a ser solo no plano de antes.
  const cancelTeam = async () => {
    try {
      await backToSolo.mutateAsync();
      await Promise.resolve(useAuthStore.getState().fetchProfile());
      showToast('Organização cancelada. Sua conta voltou a ser solo.', 'success');
      router.replace('/(tabs)/(provider)/profile' as never);
    } catch (err) {
      showToast(getApiErrorMessage(err, 'Não foi possível cancelar agora.'), 'error');
    }
  };

  const store = Platform.OS === 'ios' ? 'apple' : 'stripe';
  const floor = (members ?? []).filter((m) => m.status === 'ACTIVE' && m.attends).length;
  const current: TeamSelection | null =
    organization?.contractedProfessionals && organization.contractedClients
      ? {
          professionals: organization.contractedProfessionals,
          clients: organization.contractedClients,
        }
      : null;

  const initial: TeamSelection = {
    professionals: toCount(params.professionals) ?? current?.professionals ?? floor,
    clients: toCount(params.clients) ?? current?.clients ?? usage?.used ?? 0,
  };

  const onSubmit = async (selection: TeamSelection, appleStep: AppleLadderStep | null) => {
    try {
      if (store === 'stripe') {
        if (current && organization?.billingSource === 'STRIPE') {
          await updateSubscription.mutateAsync(selection);
          showToast('Plano da equipe atualizado.', 'success');
          router.replace('/(tabs)/(provider)/team' as never);
          return;
        }
        router.push({
          pathname: '/subscription/checkout',
          params: {
            team: '1',
            professionals: String(selection.professionals),
            clients: String(selection.clients),
          },
        } as never);
        return;
      }

      // iPhone: dentro do degrau já comprado, redistribui sem nova compra; se
      // não couber, a API recusa e compra o degrau de cima.
      if (current && organization?.billingSource === 'APPLE') {
        try {
          await redistribute.mutateAsync(selection);
          showToast('Plano da equipe atualizado.', 'success');
          router.replace('/(tabs)/(provider)/team' as never);
          return;
        } catch {
          // segue para a compra do degrau
        }
      }
      if (!appleStep) return;
      await appleIAP.purchaseTeamProduct(appleStep.productId, selection);
    } catch (err) {
      showToast(getApiErrorMessage(err, 'Não foi possível atualizar o plano agora.'), 'error');
    }
  };

  // Google Play ainda não vende o plano de equipe (preço variável).
  if (Platform.OS === 'android') {
    return (
      <ThemedText className="py-8 text-center text-sm text-neutral-500">
        O plano de equipe ainda não está disponível no app para Android.
      </ThemedText>
    );
  }

  if (isLoading) {
    return (
      <View className="items-center py-12">
        <ActivityIndicator size="large" color="#FD5001" />
      </View>
    );
  }

  if (error || !pricing) {
    return (
      <ThemedText className="py-8 text-center text-sm text-neutral-500">
        Não foi possível carregar o preço de equipe. Tente de novo em instantes.
      </ThemedText>
    );
  }

  const busy =
    updateSubscription.isPending ||
    redistribute.isPending ||
    appleIAP.state === 'purchasing' ||
    appleIAP.state === 'validating';

  return (
    <View className="gap-3">
      {!current && usage?.planName ? (
        <ThemedText className="text-center text-sm text-neutral-500">
          Plano atual: {usage.planName}
        </ThemedText>
      ) : null}
      <TeamPlanCalculator
        key={`${pricing.version.id}-${current?.professionals ?? 0}-${current?.clients ?? 0}`}
        pricing={pricing}
        initial={initial}
        floorProfessionals={floor}
        usedClients={usage?.used ?? 0}
        store={store}
        current={current}
        ctaLabel={current ? 'Atualizar plano' : 'Contratar'}
        busy={busy}
        onSubmit={(selection, step) => void onSubmit(selection, step)}
      />
      {teamPlanPending ? (
        <Pressable
          onPress={() => void cancelTeam()}
          disabled={backToSolo.isPending}
          className="items-center py-2"
        >
          <ThemedText className="text-center text-sm text-neutral-500">
            {backToSolo.isPending
              ? 'Cancelando…'
              : soloPlanName
                ? `Cancelar organização e voltar para o plano ${soloPlanName}`
                : 'Cancelar organização e voltar a ser solo'}
          </ThemedText>
        </Pressable>
      ) : null}
    </View>
  );
}
